import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CreateSentimentPayload, FeedbackItem, UpdateSentimentPayload } from './types';
import { useMutation, useQueryClient } from 'react-query';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { successMessage } from '@services';
import QueryKeys from '@constants/query-keys';
import { useModalState } from '@hooks';

export const useSentimentAnalysis = () => {
  const defaultItem = {
    id: '',
    title: '',
    url: '',
    imageUrl: '',
    label: '',
    category: 0,
    subCategory: 0,
    sentiment: 0,
    isSubmitted: false,
    categoryDescription: '',
    subCategoryDescription: '',
    companyId: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  const { id: organizationId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [editingItem, setEditingItem] = useState<FeedbackItem>(defaultItem);
  const [isOpenModal, , openModal, closeModal] = useModalState(false);

  const onRefetch = () => {
    queryClient.invalidateQueries({
      queryKey: QueryKeys.enterpriseSentimentAnalysis,
    });
  };

  const { mutate: deleteSentiment } = useMutation<unknown, unknown, string[]>(
    (uuids) =>
      ApiClientEnterprise.delete(`/enterprises-admin/${organizationId}/sentiment-analysis`, {
        data: { sentimentAnalysisUuids: uuids },
      }),
    {
      onSuccess: () => {
        successMessage('Sentiment analysis has been removed!');
        onRefetch();
        setEditingItem(defaultItem);
      },
    },
  );

  const { mutate: createSentiment, isLoading: isCreating } = useMutation({
    mutationFn: (data: CreateSentimentPayload) =>
      ApiClientEnterprise.post(`/enterprises-admin/${organizationId}/sentiment-analysis`, data),
    onSuccess: () => {
      successMessage('Sentiment analysis created successfully!');
      onRefetch();
      setEditingItem(defaultItem);
    },
  });

  const { mutate: editSentiment, isLoading: isEditing } = useMutation({
    mutationFn: (data: UpdateSentimentPayload) =>
      ApiClientEnterprise.put(`/enterprises-admin/${organizationId}/sentiment-analysis`, data),
    onSuccess: () => {
      successMessage('Sentiment analysis updated successfully!');
      onRefetch();
      setEditingItem(defaultItem);
    },
  });

  const onEdit = (row: any) => {
    setEditingItem({
      ...row,
      category: row.category.toString(),
      subCategory: row.subCategory.toString(),
      sentiment: row.sentiment.toString(),
      imageUrl: row.imageUrl ?? '',
      label: row.label ?? '',
    });
  };

  const onDelete = (row: any) => {
    deleteSentiment([row.id]);
  };

  return {
    isOpenModal,
    editingItem,
    defaultItem,
    isCreating,
    isEditing,
    openModal,
    closeModal,
    setEditingItem,
    onEdit,
    onDelete,
    createSentiment,
    editSentiment,
  };
};
